import { Button, CircularProgress } from '@mui/material'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import {
  type AuthenticationFormValues,
  getAuthenticationDomainNameForSave,
  refreshFormAfterObservabilityToolAuthentication,
  getAuthenticationRequestBody
} from 'components/status-checks/authentication-form-utils'
import {
  invokeToastOnSuccess,
  invokeToastOnUnknownError,
  setAuthenticationErrorResponse,
  shouldDisplayAuthenticationErrorNotification
} from 'components/status-checks/common/observability-tool-authentication-utils'
import { useValidateEndpointConfiguration } from 'components/status-checks/hooks/use-validate-endpoint-configuration'
import {
  buildTeamExternalIntegration,
  type StatusCheckFormValues
} from 'components/status-checks/status-checks-form-utils'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import {
  INITIAL_AUTHENTICATION_RESPONSE,
  useObservabilityToolAuthenticationContext
} from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import { useCreateAuthentication } from 'queries/external-integrations/use-create-authentication'
import { useUpdateAuthentication } from 'queries/external-integrations/use-update-authentication'
import {
  type ObservabilityToolType,
  type TeamExternalIntegration
} from 'types/api/status-checks/status-check'
import { handleDataFetchOnChange } from 'utils/handle-data-fetch-on-change'

type FormValues = StatusCheckFormValues | AuthenticationFormValues

const statusChecksUrls = getStatusChecksUrls()

export const ObservabilityToolAuthenticateAndSaveButton: React.FC<{
  externalIntegrations: TeamExternalIntegration[]
  isDisabled?: boolean
  onTestError(): void
  onTestSuccess(): void
}> = ({ externalIntegrations, isDisabled, onTestError, onTestSuccess }) => {
  const navigate = useNavigate()
  const {
    isCreatingAuthenticationOnly,
    isDrawerFlow,
    isEdit,
    setAuthenticationResponse
  } = useObservabilityToolAuthenticationContext()
  const { evaluatedTeamId, reFetchExternalIntegrations } =
    useStatusCheckExternalIntegrationsContext()

  const {
    isError: hasValidationFailed,
    isLoading: isValidating,
    validateEndpointConfiguration
  } = useValidateEndpointConfiguration(evaluatedTeamId)

  // Hooks used to create or update the authentication record depending on the `isEdit` flag.
  const {
    mutateAsync: createAuthentication,
    isError: hasCreateAuthenticationFailed
  } = useCreateAuthentication()
  const {
    mutateAsync: updateAuthentication,
    isError: hasUpdateAuthenticationFailed
  } = useUpdateAuthentication()

  const form = useFormContext<FormValues>()
  const { formState, handleSubmit, getValues } = form
  const { isDirty, isSubmitting, isValid } = formState

  const hasSaveFailed =
    hasCreateAuthenticationFailed || hasUpdateAuthenticationFailed
  const isSaveActionDisabled =
    isDisabled || !isValid || !isDirty || isValidating || isSubmitting

  // Displays the current status of the authentication process and rendered within the button as a label
  const saveButtonLabel =
    hasValidationFailed || hasSaveFailed
      ? 'Retry'
      : isValidating
      ? 'Validating Authentication...'
      : isSubmitting
      ? 'Saving...'
      : 'Save'

  /*
   * Should be invoked AFTER authentication validation is successful. Submits the create/update
   * Authentication request and handles success/error states.
   */
  const submitAuthentication = async (
    integration: TeamExternalIntegration,
    observabilityToolType: ObservabilityToolType | undefined
  ) => {
    const { name } = integration

    // Once validated, we can initiate the save/update request
    try {
      // Derive the necessary parameters to execute the mutation request
      const domain =
        getAuthenticationDomainNameForSave(getValues(), integration) ??
        undefined
      const requestBody = getAuthenticationRequestBody(integration)
      /*
       * POST or PUT mutation to update the record depending on the create/edit flow.
       * NOTE: the request params are slightly different depending on the method.
       */
      const responseData = isEdit
        ? await updateAuthentication([
            { domain, observabilityToolType, name },
            requestBody
          ])
        : await createAuthentication([
            { domain, observabilityToolType },
            requestBody
          ])

      // Re-fetch the external integrations to ensure we have the latest data
      await handleDataFetchOnChange(reFetchExternalIntegrations)

      // Display the toast notifications to share the good news and invoke the success callback
      invokeToastOnSuccess()

      !isCreatingAuthenticationOnly || isDrawerFlow
        ? // In this case, refrain from navigation. Rather update the form state with the new authentication details
          refreshFormAfterObservabilityToolAuthentication(
            responseData,
            integration,
            externalIntegrations,
            form
          )
        : // Otherwise, navigate back to the authentication list page
          navigate(statusChecksUrls.authentication())
    } catch (error: unknown) {
      !shouldDisplayAuthenticationErrorNotification(error)
        ? invokeToastOnUnknownError(error)
        : setAuthenticationResponse(
            setAuthenticationErrorResponse(
              { ...error, message: 'Authentication failed to save' },
              integration.isPrivateNetworkIntegration
            )
          )

      return Promise.reject(error)
    }
  }

  /*
   * Validating the authentication happens in two steps:
   * 1. We test the endpoint configuration (i.e., validate the credentials)
   * 2. If step 1 is successful, we then save the authentication record
   * ----------------------------------------------------------------
   * In the case we receive an error, we update the authentication response state
   * which is used to display the relevant error messaging to the user.
   */
  const handleTestAndSaveExternalIntegration = async () => {
    // Don't test if one is already in-flight
    if (isValidating) return

    // Reset any existing authentication response state as new request is initiated
    setAuthenticationResponse(INITIAL_AUTHENTICATION_RESPONSE)

    const formValues = getValues()
    const { observabilityTool } = formValues
    const integration = buildTeamExternalIntegration(formValues)

    // Define the submit callback to be invoked after successful validation
    const submitFormCallback = async () =>
      await submitAuthentication(integration, observabilityTool.type)

    try {
      await validateEndpointConfiguration(integration).then(onTestSuccess)
      await handleSubmit(submitFormCallback)()
    } catch (error) {
      onTestError()
      !shouldDisplayAuthenticationErrorNotification(error)
        ? invokeToastOnUnknownError(error)
        : setAuthenticationResponse(
            setAuthenticationErrorResponse(
              error,
              integration.isPrivateNetworkIntegration
            )
          )
    }
  }

  return (
    <Button
      data-testid="observability-tool-authenticate-and-save-button"
      disabled={isSaveActionDisabled}
      startIcon={
        (isValidating || isSubmitting) && (
          <CircularProgress color="inherit" size={16} />
        )
      }
      variant="contained"
      onClick={handleTestAndSaveExternalIntegration}
    >
      {saveButtonLabel}
    </Button>
  )
}
